import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import { Badge, Button, Card, EmptyState, Spinner } from "@/components";
import type { CrosswordListItem } from "@/lib/admin-types";
import { apiJson } from "@/lib/api";
import { formatAdminDateTime } from "@/lib/format";

function CrosswordsList() {
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["admin", "crosswords"],
    queryFn: () => apiJson<CrosswordListItem[]>("/v1/admin/crosswords"),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<{ id: number }>("/v1/admin/crosswords", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "crosswords"] });
    },
  });

  const seedVocabMutation = useMutation({
    mutationFn: () =>
      apiJson<{
        inserted: number;
        updated: number;
        total_rows: number;
        eligible_count: number;
      }>("/v1/admin/crosswords/seed-vocab", { method: "POST" }),
  });

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (listQ.isError) {
    return (
      <p className="text-sm text-red-600">{(listQ.error as Error).message}</p>
    );
  }

  const rows = listQ.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Crosswords</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create, review, regenerate, and publish daily premium crosswords.
        </p>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <Button
            disabled={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Generating…" : "Generate Draft Puzzle"}
          </Button>
          <Button
            variant="secondary"
            disabled={seedVocabMutation.isPending}
            onClick={() => seedVocabMutation.mutate()}
          >
            {seedVocabMutation.isPending
              ? "Seeding vocabulary…"
              : "Seed Crossword Vocabulary"}
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Generated puzzles start as drafts. Approve one in detail to add it to
          the daily queue. Run vocabulary seed once per environment (or after
          updating <code className="text-gray-600">vocab_seed_words.json</code>
          ) so puzzle generation has enough words.
        </p>
        {createMutation.isError ? (
          <p className="text-sm text-red-600">
            {(createMutation.error as Error).message}
          </p>
        ) : null}
        {seedVocabMutation.isError ? (
          <p className="text-sm text-red-600">
            {(seedVocabMutation.error as Error).message}
          </p>
        ) : null}
        {seedVocabMutation.isSuccess ? (
          <p className="text-sm text-green-700">
            Vocabulary seeded: {seedVocabMutation.data.inserted} inserted,{" "}
            {seedVocabMutation.data.updated} updated (
            {seedVocabMutation.data.eligible_count} eligible for crosswords).
          </p>
        ) : null}
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="No crosswords yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row) => (
            <Link
              key={row.id}
              to="/admin/crosswords/$id"
              params={{ id: String(row.id) }}
              className={clsx(
                "group block rounded-[22px] focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              )}
            >
              <Card
                className={clsx(
                  "h-full transition",
                  "group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]",
                  "group-active:scale-[0.99]",
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h2 className="text-lg font-medium text-gray-900">
                    {row.title}
                  </h2>
                  <div className="flex flex-wrap gap-1.5">
                    {row.needs_review_clue_count > 0 ? (
                      <Badge variant="warning">
                        {row.needs_review_clue_count} need rewrite
                      </Badge>
                    ) : null}
                    <Badge variant={row.is_published ? "success" : "neutral"}>
                      {row.served_on_date
                        ? "Served"
                        : row.is_published
                          ? "Queued"
                          : "Draft"}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Generated {formatAdminDateTime(row.generated_at)}
                </p>
                <p className="text-sm text-gray-500">
                  {row.served_on_date
                    ? `Served: ${row.served_on_date}`
                    : row.queue_order != null
                      ? `Queue #${row.queue_order}`
                      : "Not queued yet"}
                </p>
                <p className="text-sm text-gray-500">{row.clue_count} clues</p>
                <p className="mt-3 text-sm font-medium text-primary group-hover:underline">
                  Edit puzzle →
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/crosswords")({
  component: function CrosswordsRoute() {
    const matches = useMatches();
    const onDetail = matches.some((m) => m.routeId.includes("crosswords/$id"));
    if (onDetail) return <Outlet />;
    return <CrosswordsList />;
  },
});
